import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import "../../styles/policies.css";

const SECTIONS = [
  { id: 'privacy_policy', label: 'Privacy Policy', detail: 'Your information & how we use it', icon: 'privacy' },
  { id: 'terms_condition', label: 'Terms & Conditions', detail: 'Working together, clearly defined', icon: 'terms' },
  { id: 'delivery_policy', label: 'Shipping & Delivery', detail: 'How your deliverables reach you', icon: 'delivery' },
  { id: 'refund_policy', label: 'Cancellation & Refund', detail: 'Requests, eligibility & next steps', icon: 'refund' },
];

function PolicyIcon({ type }) {
  const paths = {
    privacy: 'M12 3 4 6v6c0 5 8 9 8 9s8-4 8-9V6l-8-3Zm-4 9 3 3 5-6',
    terms: 'M8 3H5v18h14V3h-3M8 2h8v4H8V2Zm0 8h8m-8 4h8m-8 4h5',
    delivery: 'M3 5h18v14H3V5Zm0 1 9 7 9-7M8 16h8',
    refund: 'M8 4 3 9l5 5M3 9h11a6 6 0 0 1 0 12h-3',
  };
  return <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={paths[type]} /></svg>;
}

export default function Policies() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState(SECTIONS[0].id);

  useEffect(() => {
    const id = location.hash.slice(1);
    if (!SECTIONS.some(section => section.id === id)) return;
    const element = document.getElementById(id);
    if (!element) return;
    setActiveSection(id);
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const headerHeight = document.querySelector('.mainHeader')?.getBoundingClientRect().height || 80;
    const top = element.getBoundingClientRect().top + window.scrollY - headerHeight - 24;
    window.scrollTo({ top: Math.max(0, top), behavior: reduced ? 'auto' : 'smooth' });
    element.focus({ preventScroll: true });
  }, [location.hash, location.key]);

  useEffect(() => {
    let frame;
    const update = () => {
      const threshold = (document.querySelector('.mainHeader')?.getBoundingClientRect().height || 80) + 60;
      let current = SECTIONS[0].id;
      for (const { id } of SECTIONS) {
        if (document.getElementById(id)?.getBoundingClientRect().top <= threshold) current = id;
      }
      setActiveSection(current);
    };
    const onScroll = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(update);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return (
    <div className="policies-page">
      <header className="policies-hero">
        <div className="policies-hero-inner">
          <nav className="policies-breadcrumb" aria-label="Breadcrumb"><Link to="/">Home</Link><span aria-hidden="true">/</span><span aria-current="page">Our policies</span></nav>
          <div className="policies-hero-grid">
            <div><span className="policies-hero-eyebrow">THE DETAILS THAT BUILD TRUST</span>
              <h1 className="policies-hero-title">Clear policies.<br /><em>Confident partnerships.</em></h1>
              <p className="policies-hero-sub">Understand how we protect your information, deliver our services, and support you at every step.</p>
              <div className="policies-hero-meta"><span>04 policy documents</span><span aria-hidden="true">/</span><span>All in one place</span></div>
            </div>
            <div className="policies-hero-note"><div className="policies-note-icon"><PolicyIcon type="terms" /></div><span className="policies-note-label">OUR POLICIES</span><p>A clear foundation<br />for working together.</p><span className="policies-note-footer">SURYA GROUP <span aria-hidden="true">↗</span></span></div>
          </div>
        </div>
      </header>

      <div className="policies-overview" aria-label="Policy overview">
        {SECTIONS.map(({ id, label, detail, icon }, index) => <Link to={`#${id}`} className="policies-overview-link" key={id}><span className="policies-overview-top"><PolicyIcon type={icon} /><span>0{index + 1}</span></span><strong>{label}</strong><span className="policies-overview-detail">{detail}</span><span className="policies-overview-arrow" aria-hidden="true">↗</span></Link>)}
      </div>

      <div className="policies-layout">
        <aside className="policies-sidebar">
          <p className="policies-sidebar-label">IN THIS GUIDE</p>
          <nav className="policies-sidebar-nav" aria-label="Policy sections">
            {SECTIONS.map(({ id, label }, index) => <Link key={id} to={`#${id}`} className={`policies-sidebar-link${activeSection === id ? ' active' : ''}`} aria-current={activeSection === id ? 'location' : undefined}><span>0{index + 1}</span>{label}<span className="policies-sidebar-arrow" aria-hidden="true">↗</span></Link>)}
          </nav>
          <div className="policies-mobile-nav"><label htmlFor="policy-jump">Jump to a policy</label><select id="policy-jump" value={activeSection} onChange={event => navigate(`#${event.target.value}`)}>{SECTIONS.map(({ id, label }) => <option key={id} value={id}>{label}</option>)}</select></div>
          <div className="policies-sidebar-help"><PolicyIcon type="delivery" /><h2>Need a little clarity?</h2><p>Our team can help with questions about these policies.</p><Link to="/contact-us">Talk to our team <span aria-hidden="true">↗</span></Link></div>
          <button type="button" className="policies-print" onClick={() => window.print()}>Print or save as PDF <span aria-hidden="true">↗</span></button>
        </aside>

        {/* Content */}
        <div className="policies-content">
          {/* Privacy Policy */}
          <article className="policy-card" id="privacy_policy" tabIndex={-1} aria-labelledby="privacy_policy-title">
            <div className="policy-card-header"><span className="policy-section-number" aria-hidden="true">01</span>
              <div className="policy-icon"><PolicyIcon type="privacy" /></div>
              <div>
                <h2 className="policy-card-title" id="privacy_policy-title">Privacy Policy</h2>
                <span className="policy-badge">Data Protection</span>
              </div>
            </div>
            <div className="policy-body">
              <p>Welcome to Suryagroup. Your privacy is important to us, and we are committed to protecting your personal information. This Privacy Policy outlines how we collect, use, and safeguard your data when you use our services.</p>

              <h3>Information We Collect</h3>
              <p>We may collect personal and business details such as your name, email address, phone number, and payment information to provide our services.</p>

              <h3>How We Use Your Information</h3>
              <ul>
                <li>To process transactions securely</li>
                <li>To improve our services</li>
                <li>To communicate updates and offers</li>
              </ul>

              <h3>Data Security</h3>
              <p>We use industry-standard security measures to protect your information.</p>

              <h3>Contact Us</h3>
              <p>If you have any questions about our privacy practices, please email us at <a href="mailto:Info@suryagroup.us">Info@suryagroup.us</a>.</p>
            </div>
          </article>

          {/* Terms & Conditions */}
          <article className="policy-card" id="terms_condition" tabIndex={-1} aria-labelledby="terms_condition-title">
            <div className="policy-card-header"><span className="policy-section-number" aria-hidden="true">02</span>
              <div className="policy-icon"><PolicyIcon type="terms" /></div>
              <div>
                <h2 className="policy-card-title" id="terms_condition-title">Terms and Conditions</h2>
                <span className="policy-badge">Service Agreement</span>
              </div>
            </div>
            <div className="policy-body">
              <p>Welcome to Suryagroup. We appreciate the trust you place in us, and we are committed to delivering reliable, accurate, and professional services. These Terms and Conditions outline the rules and guidelines for using our website and services. By accessing or using our services, you agree to these terms in their entirety.</p>

              <h3>1. Scope of Services</h3>
              <p>We specialize in providing accurate construction estimating, takeoff services, and preconstruction consultancy. Our services are tailored to meet the needs of general contractors, subcontractors, and project managers. We promise to deliver our services with professionalism, honesty, and dedication to your project's success.</p>

              <h3>2. User Responsibilities</h3>
              <p>When using our services, you agree to:</p>
              <ul>
                <li>Provide accurate and complete project details, including drawings, specifications, and any other relevant information.</li>
                <li>Respond to our queries in a timely manner to ensure smooth project progression.</li>
                <li>Use our deliverables for their intended purposes and avoid redistribution or unauthorized use.</li>
              </ul>

              <h3>3. Payments and Invoicing</h3>
              <p>We aim to keep our payment terms simple and fair:</p>
              <ul>
                <li>All payments must be made through secure channels, including Razorpay or direct bank transfers.</li>
                <li>Our invoices are due within the timeline specified in the contract or service agreement.</li>
                <li>For custom services, a deposit may be required before the project begins.</li>
              </ul>
              <p>If you encounter issues with payments or require clarification, please email us at <a href="mailto:Info@suryagroup.us">Info@suryagroup.us</a>.</p>

              <h3>4. Project Timelines</h3>
              <p>We are committed to delivering on time. Project timelines will be clearly outlined in the service agreement. If unforeseen circumstances arise, we will communicate with you immediately and work to resolve any delays.</p>

              <h3>5. Cancellation Policy</h3>
              <p>We understand that project needs can change. Cancellations must be requested within 24 hours of placing an order. Please email us at <a href="mailto:Info@suryagroup.us">Info@suryagroup.us</a> to process your request.</p>

              <h3>6. Refund Policy</h3>
              <p>Your satisfaction is our priority. Refunds will be issued in the following cases:</p>
              <ul>
                <li>If services have not yet been initiated.</li>
                <li>If there was a clear mistake on our end in delivering the agreed-upon services.</li>
              </ul>
              <p>Refund requests are reviewed on a case-by-case basis and processed within 7 business days upon approval.</p>

              <h3>7. Confidentiality</h3>
              <p>We take confidentiality seriously. All documents, plans, and information shared with us will be handled with strict confidentiality. We will never disclose your data to third parties without your consent.</p>

              <h3>8. Limitation of Liability</h3>
              <p>While we strive for excellence, we cannot be held liable for unforeseen issues such as inaccuracies in project details provided by you, third-party delays, or external factors beyond our control. However, we will work closely with you to find solutions should any issues arise.</p>

              <h3>9. Customer Support</h3>
              <p>We are here to help. If you have questions, concerns, or need support, feel free to reach out to us at <a href="mailto:Info@suryagroup.us">Info@suryagroup.us</a>. Our team is committed to providing prompt and professional assistance.</p>

              <h3>10. Modifications to Terms</h3>
              <p>We reserve the right to update or modify these Terms and Conditions at any time. Any changes will be communicated on our website. We encourage you to review these terms periodically to stay informed.</p>

              <h3>11. Governing Law</h3>
              <p>These Terms and Conditions are governed by the laws of India. Any disputes will be resolved amicably or through appropriate legal channels within Delhi's jurisdiction.</p>

              <h3>12. Final Note</h3>
              <p>Thank you for choosing Suryagroup. We value your trust and are dedicated to exceeding your expectations. Your success is our success!</p>
              <p>For any further queries, please contact us at <a href="mailto:Info@suryagroup.us">Info@suryagroup.us</a>.</p>
            </div>
          </article>

          {/* Shipping & Delivery */}
          <article className="policy-card" id="delivery_policy" tabIndex={-1} aria-labelledby="delivery_policy-title">
            <div className="policy-card-header"><span className="policy-section-number" aria-hidden="true">03</span>
              <div className="policy-icon"><PolicyIcon type="delivery" /></div>
              <div>
                <h2 className="policy-card-title" id="delivery_policy-title">Shipping and Delivery Policy</h2>
                <span className="policy-badge">Deliverables</span>
              </div>
            </div>
            <div className="policy-body">
              <p>This policy applies to any deliverables as part of our service agreement.</p>

              <h3>Delivery Timeline</h3>
              <p>Deliverables will be sent within the agreed project timeline, typically via email or Client user platforms. It will launch soon in our website.</p>

              <h3>Shipping Charges</h3>
              <p>No physical shipping is applicable; hence, no charges apply.</p>

              <h3>Contact Us</h3>
              <p>For delivery concerns, email us at <a href="mailto:Info@suryagroup.us">Info@suryagroup.us</a>.</p>
            </div>
          </article>

          {/* Cancellation & Refund */}
          <article className="policy-card" id="refund_policy" tabIndex={-1} aria-labelledby="refund_policy-title">
            <div className="policy-card-header"><span className="policy-section-number" aria-hidden="true">04</span>
              <div className="policy-icon"><PolicyIcon type="refund" /></div>
              <div>
                <h2 className="policy-card-title" id="refund_policy-title">Cancellation and Refund Policy</h2>
                <span className="policy-badge">Refunds</span>
              </div>
            </div>
            <div className="policy-body">
              <p>At Suryagroup, we aim to deliver high-quality construction estimating and consulting services. We understand that circumstances may change, and this policy is designed to address your concerns with cancellations and refunds in a fair and transparent manner.</p>

              <h3>1. Cancellation Policy</h3>
              <p>We allow cancellations for services that have not yet been initiated. Please note the following conditions:</p>
              <ul>
                <li>Cancellations must be requested within <strong>24 hours</strong> of placing an order.</li>
                <li>If the service has already begun, cancellations will not be accepted.</li>
                <li>Cancellations should be requested by sending an email to <a href="mailto:Info@suryagroup.us">Info@suryagroup.us</a> with the required details.</li>
              </ul>
              <p>Once we confirm your cancellation request, you will receive an acknowledgment email. If your cancellation is approved, we will inform you about the next steps.</p>

              <h3>2. Refund Policy</h3>
              <p>Your satisfaction is our priority. Refunds will be processed under the following conditions:</p>
              <ul>
                <li><strong>Before Service Initiation:</strong> Full refunds will be provided for cancellations made before any work has started.</li>
                <li><strong>Partial Refunds:</strong> If a service is partially completed, we will evaluate the completed work and issue a refund for the unused portion of the service.</li>
                <li><strong>Non-Refundable Services:</strong> Once the deliverables (e.g., estimates, reports, takeoffs) have been submitted, refunds will not be issued unless otherwise stated below.</li>
                <li><strong>Scope of Work Compliance:</strong> If the job has been submitted and the takeoff does not meet the agreed-upon scope of work, you may contact us to discuss your concerns. Based on our review, we will consider issuing a full or partial refund depending on the quality of the deliverables and your level of satisfaction.</li>
              </ul>
              <p>Refunds will be processed within <strong>7 business days</strong> after the refund is approved. Refunds will be issued through the same payment method used during the purchase.</p>

              <h3>3. Exceptions</h3>
              <p>Refunds will not be provided in the following cases:</p>
              <ul>
                <li>If the customer fails to provide complete and accurate project information needed to complete the service.</li>
                <li>If delays or issues arise due to factors outside Suryagroup's control (e.g., incomplete data, third-party delays).</li>
                <li>For changes in scope or requirements after the project has started.</li>
              </ul>

              <h3>4. How to Request a Cancellation or Refund</h3>
              <p>To initiate a cancellation or refund request, please follow these steps:</p>
              <ol>
                <li>Send an email to <a href="mailto:Info@suryagroup.us">Info@suryagroup.us</a> with the subject line "Cancellation/Refund Request".</li>
                <li>Include the following details in your email:
                  <ul>
                    <li>Your Name</li>
                    <li>Business Name</li>
                    <li>Project Details</li>
                    <li>Scope of Work</li>
                    <li>Reason for Your Request (provide detailed information)</li>
                  </ul>
                </li>
                <li>Our support team will review your request and respond within 2–3 business days.</li>
              </ol>
              <p>We strive to resolve your concerns quickly and fairly.</p>

              <h3>5. Our Commitment to Customer Satisfaction</h3>
              <p>We are dedicated to providing excellent service and addressing your concerns promptly. If you feel your issue has not been resolved to your satisfaction, please escalate the matter by emailing us at <a href="mailto:Info@suryagroup.us">Info@suryagroup.us</a>.</p>

              <h3>6. Contact Us</h3>
              <p>If you have any questions about this policy or need further assistance, please reach out to us at:</p>
              <p><strong>Email:</strong> <a href="mailto:Info@suryagroup.us">Info@suryagroup.us</a></p>
              <p>We value your trust and strive to ensure a seamless experience with our services.</p>
            </div>
          </article>

          {/* Footer CTA */}
          <div className="policies-cta">
            <div><span className="policies-hero-eyebrow">HERE TO HELP</span><h2>Let’s clear things up.</h2><p>Have a question about a policy? Start a conversation with our team.</p></div>
            <a href="mailto:Info@suryagroup.us" className="policies-cta-btn">Email our team <span aria-hidden="true">↗</span></a>
          </div>
        </div>
      </div>
    </div>
  );
}
